import { EventEmitter, Injectable } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { CreateTreatmentModel, TreatmentMethodModel, TreatmentModel, UpdateTreatmentModel } from "../../global-data-models/treatment.model";
import { TreatmentService } from "../../global-services/treatment.service";
import { GlobalMessageService } from "../../global-services/global-message.service";
import { Misc } from "../../global-misc/misc";
import { GlobalLoadingService } from "../../global-services/global-loading.service";

export type CrudTreatmentMode = "create" | "update" | "end";

export interface CrudTreatmentState {
    form: FormGroup<any>;
    crudMode: CrudTreatmentMode;
    orgId: string;
    patientId: string;
    treatmentId?: string;
    treatmentMethods: TreatmentMethodModel[];
};

@Injectable({ providedIn: "root" })
export class CrudTreatmentFacade {

    private stateEvent = new EventEmitter<CrudTreatmentState>();
    public crudTreatmentState$ = this.stateEvent.asObservable();

    public uiState!: CrudTreatmentState;
    public readonly EMPTY_UI_STATE: CrudTreatmentState = {
        form: this.createForm("create", undefined),
        crudMode: "create",
        orgId: "",
        patientId: "",
        treatmentMethods: []
    }

    public constructor(
        private readonly fb: FormBuilder,
        private readonly treatmentService: TreatmentService,
        private readonly messageService: GlobalMessageService,
        private readonly loadingService: GlobalLoadingService
    ) { }

    private updateState(newState: Partial<CrudTreatmentState>) {
        this.uiState = { ...this.uiState, ...newState };
        this.stateEvent.emit(this.uiState);
    }

    public async begin(_crudMode: CrudTreatmentMode, _orgId: string, _patientId: string, _treatmentId?: string) {
        this.loadingService.show();

        try {
            let treatmentData: TreatmentModel | undefined = undefined;
            if (_crudMode === "end" || _crudMode === "update") {
                treatmentData = await this.treatmentService.getPatientTreatment(_orgId, _patientId, _treatmentId!);
            }

            const _treatmentMethods: TreatmentMethodModel[] = await this.treatmentService.getTreatmentMethods();

            this.updateState({
                form: this.createForm(_crudMode, treatmentData),
                crudMode: _crudMode,
                orgId: _orgId,
                patientId: _patientId,
                treatmentId: _treatmentId,
                treatmentMethods: _treatmentMethods,
            });
        } catch (error: unknown) {
            console.log(error);
            const errorMsg: string = Misc.getErrorMessage(error);
            this.messageService.showError("Treatment Initialisation Error", errorMsg);

            this.updateState(this.EMPTY_UI_STATE);
        } finally {
            this.loadingService.hide();
        }
    }

    private endDateValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const formGroup = control as FormGroup;
            if (!formGroup.controls) {
                return null;
            }

            const startDate = formGroup.get('startDate')?.value;
            const endDate = formGroup.get('endDate')?.value;

            if (!endDate || !startDate) {
                return null; // Don't validate if either date is missing
            }

            const startDateTime = new Date(startDate).getTime();
            const endDateTime = new Date(endDate).getTime();

            if (endDateTime < startDateTime) {
                return { endDateBeforeStartDate: true };
            }

            return null;
        };
    }

    private createForm(crudMode: CrudTreatmentMode, treatmentData?: TreatmentModel): FormGroup<any> {
        const form = this.fb.group(
            {
                treatmentMethodId: [{ value: treatmentData?.treatment_method_id || "", disabled: crudMode === "end" }, Validators.required],
                startDate: [
                    { value: treatmentData?.start_date ? new Date(treatmentData?.start_date) : new Date(), disabled: crudMode === "end" },
                    Validators.required
                ],
                endDate: [crudMode === "end" ? new Date() : (treatmentData?.end_date ? new Date(treatmentData?.end_date) : null)],
            },
            { validators: this.endDateValidator() }
        );

        return form;
    }

    private formToTreatmentModel(): CreateTreatmentModel | UpdateTreatmentModel {
        const rawValues = this.uiState.form.getRawValue();

        const baseModel: Record<string, any> = {
            patient_id: this.uiState.patientId,
            treatment_method_id: rawValues.treatmentMethodId,
            start_date: rawValues.startDate,
            end_date: rawValues.endDate
        }

        if (this.uiState.crudMode === "update") {
            return { ...baseModel, id: this.uiState.treatmentId! } as UpdateTreatmentModel;
        }

        return baseModel as CreateTreatmentModel;
    }

    public async submitForm() {
        this.loadingService.show();

        const treatmentModel: CreateTreatmentModel | UpdateTreatmentModel = this.formToTreatmentModel();

        try {
            let response;

            if (this.uiState.crudMode === "create") {
                response = await this.treatmentService.createPatientTreatment(this.uiState.orgId, this.uiState.patientId,
                    treatmentModel as CreateTreatmentModel);
            }

            if (this.uiState.crudMode === "update" || this.uiState.crudMode === "end") {
                response = await this.treatmentService.updatePatientTreatment(this.uiState.orgId, this.uiState.patientId,
                    this.uiState.treatmentId!, treatmentModel as UpdateTreatmentModel);
            }

            if (!response) {
                throw new Error("Got no response");
            }
        } catch (error: unknown) {
            const errorMsg: string = Misc.getErrorMessage(error);
            this.messageService.showError(`${Misc.captilizeFirstLetter(this.uiState.crudMode)} Treatment Error`, errorMsg);
        } finally {
            this.loadingService.hide();
            this.messageService.showSuccess("Add/Update Treatment Success", `Treatment ${this.uiState.crudMode} success!`);
        }

    }
}